import { CommonModal } from 'components/common';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import './dropdownSelectModal.css';
import { IoMdRadioButtonOn } from 'react-icons/io';
import { FaPlus } from 'react-icons/fa';
import StorageService from 'utils/StorageServices';
import { getVehicleGroupListData } from '_services/accountsServices/vehicleGroupServices';
import { getUser } from '_services';
import FirmImg from 'assets/img/firm.png';

const LIMIT = 20;

const DropdownSelectModal = (props) => {
    const {
        isOpen,
        closeModal = () => {},
        onSubmit  = () => {},
        fetchListFn = null,
    } = props;

    const companyIdRef = useRef(StorageService.getCompanyId());
    const firmIdRef    = useRef(StorageService.getFirmId());

    const [listArray,   setListArray]   = useState([]);
    const [loading,     setLoading]     = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMore,     setHasMore]     = useState(true);
    const [currentData, setCurrentData] = useState({});
    const [search,      setSearch]      = useState('');
    const [offset,      setOffset]      = useState(1);

    const scrollRef      = useRef(null);
    const searchTimerRef = useRef(null);

    // ─── Fetchers ─────────────────────────────────────────────────────────────

    const fetchUsers = async (searchVal, pageOffset, append) => {
        try {
            const response = await getUser({
                company_id: companyIdRef.current,
                offset: pageOffset,
                limit: LIMIT,
                search: searchVal,
                firm_id: firmIdRef.current || '',
            });
            if (response?.responseCode === 200) {
                const users = response.data.user_list.map((user) => ({
                    label: user.company_user_name,
                    value: user?.user_id?._id,
                    subTexts: [user?.email || '', user?.role || ''].filter(Boolean),
                }));
                setHasMore(users.length === LIMIT);
                setListArray((prev) => append ? [...prev, ...users] : users);
            }
        } catch (_) {}
    };

    const fetchVehicleGroups = async (searchVal, pageOffset, append) => {
        try {
            const res = await getVehicleGroupListData({
                company_id: companyIdRef.current,
                search: searchVal,
                offset: pageOffset,
                limit: LIMIT,
            });
            if (res?.responseCode === 200) {
                const groups = res.data.vehicle_group_list.map((el) => ({
                    label: el?.name,
                    value: el?._id,
                    subTexts: [
                        el?.description || '',
                        el?.vehicle_count ? `${el.vehicle_count} vehicles` : '',
                    ].filter(Boolean),
                }));
                setHasMore(groups.length === LIMIT);
                setListArray((prev) => append ? [...prev, ...groups] : groups);
            }
        } catch (_) {}
    };

    const fetchCustom = async (searchVal, pageOffset, append) => {
        try {
            const result = await fetchListFn(searchVal, pageOffset, LIMIT);
            setHasMore(result?.hasMore ?? false);
            setListArray((prev) => append ? [...prev, ...(result?.list || [])] : result?.list || []);
        } catch (_) {}
    };

    // ─── Core load ────────────────────────────────────────────────────────────

    const loadData = useCallback(async (searchVal, pageOffset, append = false) => {
        append ? setLoadingMore(true) : setLoading(true);

        if (fetchListFn) {
            await fetchCustom(searchVal, pageOffset, append);
        } else if (isOpen?.type === 'updated_user_id') {
            await fetchUsers(searchVal, pageOffset, append);
        } else if (isOpen?.type === 'vehicle_group_id') {
            await fetchVehicleGroups(searchVal, pageOffset, append);
        }

        append ? setLoadingMore(false) : setLoading(false);
    }, [isOpen?.type, fetchListFn]);

    // ─── Search debounce ──────────────────────────────────────────────────────

    useEffect(() => {
        if (!isOpen?.action) return;
        clearTimeout(searchTimerRef.current);
        searchTimerRef.current = setTimeout(() => {
            setOffset(1);
            setHasMore(true);
            loadData(search, 1, false);
        }, 350);
        return () => clearTimeout(searchTimerRef.current);
    }, [search, isOpen?.type, isOpen?.action]);

    // ─── Reset on open ────────────────────────────────────────────────────────

    useEffect(() => {
        setCurrentData(isOpen?.data?.value ? isOpen.data : {});
        setSearch('');
        setListArray([]);
        setOffset(1);
    }, [isOpen?.type, isOpen?.action]);

    // ─── Infinite scroll ──────────────────────────────────────────────────────

    const handleScroll = useCallback(() => {
        const el = scrollRef.current;
        if (!el || loadingMore || !hasMore) return;
        if (el.scrollTop + el.clientHeight >= el.scrollHeight - 40) {
            const next = offset + 1;
            setOffset(next);
            loadData(search, next, true);
        }
    }, [offset, loadingMore, hasMore, search, loadData]);

    useEffect(() => {
        const el = scrollRef.current;
        if (el) el.addEventListener('scroll', handleScroll);
        return () => { if (el) el.removeEventListener('scroll', handleScroll); };
    }, [handleScroll]);

    // ─── Handlers ─────────────────────────────────────────────────────────────

    const handleOnChange = (data) => {
        setCurrentData(data);
        onSubmit({ [isOpen?.type]: data });
        closeModal(false);
    };

    // Add New — same as CityDropdownForTrip ka addButtonAction
    const handleAddNew = () => {
        if (typeof isOpen?.onAddNew === 'function') {
            isOpen.onAddNew();
            closeModal(false);
        }
    };

    // Edit item — same as CityDropdownForTrip ka onEdit(item)
    const handleEditItem = (e, item) => {
        e.stopPropagation();
        if (typeof isOpen?.onEdit === 'function') {
            isOpen.onEdit(item);
            closeModal(false);
        }
    };

    // ─── Render ───────────────────────────────────────────────────────────────

    return (
        <CommonModal {...props} isFooter={false}>
            <div className="__modalSel_content">

                {/* Search — existing CSS */}
                <div className="__modalSel_search">
                    <input
                        type="text"
                        placeholder="Search"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        autoFocus
                    />
                </div>

                {/* Add New — same as CityDropdownForTrip ka isAddButton */}
                {isOpen?.showAddNew && (
                    <div className="__modalSel_addNew" onClick={handleAddNew}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 18 18" fill="none">
                            <path d="M8.61539 0C6.33902 0.0276205 4.16368 0.944178 2.55393 2.55393C0.944178 4.16368 0.0276205 6.33902 0 8.61539C0.0276205 10.8917 0.944178 13.0671 2.55393 14.6768C4.16368 16.2866 6.33902 17.2031 8.61539 17.2308C10.8917 17.2031 13.0671 16.2866 14.6768 14.6768C16.2866 13.0671 17.2031 10.8917 17.2308 8.61539C17.2031 6.33902 16.2866 4.16368 14.6768 2.55393C13.0671 0.944178 10.8917 0.0276205 8.61539 0ZM13.5385 9.23077H9.23077V13.5385H8V9.23077H3.69231V8H8V3.69231H9.23077V8H13.5385V9.23077Z" fill="#1956F6" />
                        </svg>
                        <span>Add New {isOpen?.name || ''}</span>
                    </div>
                )}

                {/* List — existing CSS classes unchanged */}
                <ul className="__modalSelListUl" ref={scrollRef}>

                    {loading ? (
                        <li className="__modalSelListLi" style={{ justifyContent: 'center' }}>
                            <div className="__modalSel_spinner" />
                        </li>
                    ) : listArray.length > 0 ? (
                        <>
                            {listArray.map((item, index) => {
                                const isActive = currentData?.value === item.value;
                                return (
                                    <li
                                        key={item.value + index}
                                        className={`__modalSelListLi${isActive ? ' active' : ''} __modalSelListLi_hoverable`}
                                        onClick={() => handleOnChange(item)}
                                    >
                                        <div className="__modalSelListFlx">
                                            <IoMdRadioButtonOn />
                                            <div className="__modalSel_itemWrap">
                                                <span>{item.label}</span>
                                                {item.subTexts?.length > 0 && (
                                                    <div className="__modalSel_subTexts">
                                                        <img
                                                            src={FirmImg}
                                                            className="firm_icon_img"
                                                            style={{ filter: "invert(60%) sepia(90%) saturate(900%) hue-rotate(5deg)" }}
                                                        />
                                                        {item.subTexts.map((txt, i) => (
                                                            <span key={i} className="__modalSel_subText">
                                                                {txt}
                                                                {i < item.subTexts.length - 1 && (
                                                                    <span className="__modalSel_subDot">·</span>
                                                                )}
                                                            </span>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Edit icon — same as CityDropdownForTrip ka onEdit, sirf jab prop diya ho */}
                                        {isOpen?.onEdit && (
                                            <span
                                                className="__modalSel_itemEdit"
                                                onClick={(e) => handleEditItem(e, item)}
                                                title={`Edit ${item.label}`}
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                                </svg>
                                            </span>
                                        )}
                                    </li>
                                );
                            })}

                            {loadingMore && (
                                <li className="__modalSelListLi" style={{ justifyContent: 'center' }}>
                                    <div className="__modalSel_spinner" />
                                </li>
                            )}

                            {!hasMore && !loadingMore && (
                                <li className="__modalSel_endMsg">No more results</li>
                            )}
                        </>
                    ) : (
                        <li className="__modalSelListLi" style={{ justifyContent: 'center' }}>
                            <span>No data found</span>
                        </li>
                    )}

                </ul>
            </div>
        </CommonModal>
    );
};

export default DropdownSelectModal;